import invariant from 'invariant';
import { isNumber } from 'lodash';
// @todo (WEB-1045): Add condensed UUID support to routes
// import { toCondensedUUID } from '@frameio/utils/formatters/uuid';
import type { ValidHref } from '../types';

// @todo (WEB-1045): Remove when condensed UUID support is added to routes
const toCondensedUUID = (uuid: string): string => uuid;

/**
 * Folder Viewer Page
 * Viewer page when accessing the asset from a folder
 * /project/:projectId/view/:assetId
 *
 * Optional query params:
 * - commentId
 * - versionId
 * - compare
 * - activeIndex
 * - t
 * - d
 */
export type FolderViewerGetLinkParams = {
  page: 'folderViewer';
  projectId: string;
  assetId: string;
  /**
   * Deeplinking to a specific version
   */
  versionId?: string;
  /**
   * Deeplinking to a specific minor version / edit within the active version
   */
  editId?: string;
  /**
   * Deeplinking to a specific comment
   */
  commentId?: string;
  /**
   * Deeplinking to comparison viewer
   */
  compare?: string[];
  /**
   * Deeplinking to comparison viewer with a specific index active
   */
  activeIndex?: number;
  /**
   * Deeplinking to a specific time in the media viewer (microseconds)
   */
  t?: number;
  /**
   * Deeplinking to a specific range duration in the media viewer (seconds)
   */
  d?: number;
};

export const getLinkForFolderViewer = (
  params: FolderViewerGetLinkParams
): ValidHref => {
  // /project/:projectId/view/:assetId
  const { projectId, assetId } = params;
  invariant(projectId, 'projectId is required');
  invariant(assetId, 'assetId is required');

  const _projectId = toCondensedUUID(projectId);
  const _assetId = toCondensedUUID(assetId);
  let newUrl: ValidHref = `/project/${_projectId}/view/${_assetId}`;

  const urlSearchParams = new URLSearchParams();

  // ... ?c=:commentId
  if ('commentId' in params && params.commentId) {
    urlSearchParams.set('c', toCondensedUUID(params.commentId));
  }

  // ... ?versionId=:versionId
  if ('versionId' in params && params.versionId) {
    urlSearchParams.set('version', toCondensedUUID(params.versionId));
  }

  // ... ?edit=:editId
  if ('editId' in params && params.editId) {
    urlSearchParams.set('edit', toCondensedUUID(params.editId));
  }

  // ... ?t=:t
  if ('t' in params && isNumber(params.t)) {
    urlSearchParams.set('t', params.t.toString());
  }

  // ... ?d=:d
  if ('d' in params && isNumber(params.d)) {
    urlSearchParams.set('d', params.d.toString());
  }

  // ... ?activeIndex=:activeIndex
  if ('activeIndex' in params && isNumber(params.activeIndex)) {
    urlSearchParams.set('activeIndex', params.activeIndex.toString());
  }

  // ... ?compare=:comparisonId1&compare=comparisonId2
  if ('compare' in params && params.compare) {
    params.compare.forEach((param) => {
      urlSearchParams.append('compare', toCondensedUUID(param));
    });
  }

  if (urlSearchParams.toString()) {
    newUrl += `?${
      urlSearchParams.toString() as {
        c?: string;
        version?: string;
        edit?: string;
        compare?: string;
        activeIndex?: string;
        t?: string;
        d?: string;
      }
    }`;
  }
  return newUrl as ValidHref;
};

export const isFolderViewerPageType = (href: ValidHref): boolean => {
  // Checks if a string matches the pattern /project/:projectId/view/:assetId/<anything>
  const parts = href.split('/');
  return parts[1] === 'project' && parts[3] === 'view';
};
